import { DAEMON_URL } from "../config";

/**
 * Bob's full-pack seed review, through the daemon's proxy. Starting one is
 * admin-only and spends money; members may read the status and poll a job, and
 * get `cost_usd` as null.
 */

/** `refused`: the pass lock stayed held, so the seed never started and spent nothing. */
export type SeedJobState = "queued" | "running" | "done" | "failed" | "refused";

export type SeedPillState = "running" | "queued" | "cooldown" | "daily_cap" | "queue_full" | "seeding_off" | "idle";

export interface SeedJob {
  id: number;
  /** Typed loosely so a state Bob adds later still parses. */
  state: string;
  /** 1-based place among live jobs, the running one first; 0 once finished. */
  position?: number;
  created_at?: string;
  updated_at?: string;
  cost_usd?: number | null;
  error?: string | null;
  /** A claim made by whoever called Bob, never a verified identity. Absent for a member. */
  started_by?: string | null;
  source?: string | null;
}

export interface SeedStatus {
  state: string;
  /** The live job, or the latest finished one, or null. */
  job: SeedJob | null;
  seeded?: boolean;
  retry_at?: string | null;
  retry_after_sec?: number | null;
  attempts_24h?: number;
  limits?: { cooldown_sec?: number; daily_cap?: number; queue_max?: number };
}

/** A refusal or failure from the seed routes. `body` is the daemon's answer,
 *  so a caller can read Bob's `state`, `retry_at` and a live `job` off it. */
export class SeedError extends Error {
  constructor(public status: number, public body: Record<string, unknown>) {
    super(
      typeof body.message === "string" && body.message
        ? body.message
        : typeof body.error === "string" && body.error
          ? body.error
          : body.configured === false
            ? "Code review is not connected"
            : `http ${status}`,
    );
    this.name = "SeedError";
  }
}

const PLAIN: Record<string, string> = {
  seed_runner_not_wired: "This Bob cannot run whole-pack reviews.",
  pr_reviewer_unreachable: "The dashboard could not reach Bob.",
  pr_reviewer_auth: "Bob refused the dashboard's credentials.",
  pr_reviewer_bad_body: "Bob answered with something this page cannot read.",
  seeding_off: "Full reviews are turned off for this pack.",
  already_seeded: "Bob has reviewed this pack before, and this Bob runs another whole-pack review only when forced.",
};

/** A seed failure as a sentence: Bob's known codes in plain words, anything else verbatim. */
export function seedErrorText(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  const code = /^[a-z_]+/.exec(msg)?.[0];
  return code !== undefined && Object.hasOwn(PLAIN, code) ? PLAIN[code]! : msg;
}

/** 502s that asking again cannot change: a token Bob refuses, a body nobody can read. */
const FINAL_CODES = new Set(["pr_reviewer_auth", "pr_reviewer_bad_body"]);

/** Worth asking again: the network failed, or the daemon or Bob had a 5xx other than 501 (not built). */
export function seedRetryable(e: unknown): boolean {
  if (!(e instanceof SeedError)) return true;
  if (typeof e.body.error === "string" && FINAL_CODES.has(e.body.error)) return false;
  return e.status >= 500 && e.status !== 501;
}

async function call(path: string, init: RequestInit): Promise<Record<string, unknown>> {
  const res = await fetch(`${DAEMON_URL}${path}`, { ...init, credentials: "include" });
  let body: Record<string, unknown> = {};
  try {
    const parsed: unknown = await res.json();
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) body = parsed as Record<string, unknown>;
  } catch { /* keep the empty body; the status still says what happened */ }
  if (!res.ok) throw new SeedError(res.status, body);
  return body;
}

function seedPath(project: string, suffix = ""): string {
  return `/projects/${encodeURIComponent(project)}/code-review/seed${suffix}`;
}

export async function getSeedStatus(project: string): Promise<SeedStatus> {
  const body = await call(seedPath(project), { method: "GET", headers: { Accept: "application/json" } });
  return {
    ...(body as unknown as SeedStatus),
    state: typeof body.state === "string" ? body.state : "unknown",
    job: body.job && typeof body.job === "object" ? (body.job as SeedJob) : null,
  };
}

/**
 * Start a full-pack review. Admin only. `force` lifts Bob's cooldown and never
 * the daily cap. Throws SeedError for every refusal, carrying Bob's body.
 */
export async function startSeed(project: string, force: boolean): Promise<SeedJob> {
  const body = await call(seedPath(project), {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(force ? { force: true } : {}),
  });
  if (!body.job || typeof body.job !== "object") throw new SeedError(502, { error: "Bob answered without a job" });
  return body.job as SeedJob;
}

export async function getSeedJob(project: string, id: number): Promise<SeedJob> {
  const body = await call(seedPath(project, `/${id}`), { method: "GET", headers: { Accept: "application/json" } });
  if (!body.job || typeof body.job !== "object") throw new SeedError(502, { error: "Bob answered without a job" });
  return body.job as SeedJob;
}
